import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useGlobalLoader } from "./GlobalLoader";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";
import inovaone_api from "@/services/inovaone_api";
import { alert } from "@/contexts/AlertModalContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { useTranslation } from "react-i18next";
import { MENU_SECTIONS, PROTECTED_MENU_URLS } from "@/config/menuSections";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ExternalLink,
  Eye,
  HelpCircle,
  ImageOff,
  Loader2,
  Plus,
  Save,
  Search,
  Settings,
  Sparkles,
  X,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { TratativasImpressaoSection } from "./TratativasImpressaoSection";
import { Separator } from "./ui/separator";
import { converterListaZplItemParaUrl } from "@/utils/UtilFuncoes";
import { useCategorias } from "@/contexts/CategoriasContext";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import { useEpc } from "@/contexts/EpcContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Card, CardContent } from "./ui/card";
import { AdminContext } from "@/contexts/AdminContext";
import {
  parseOpcoesInfoCompl,
  parseInfoComplObrigatorios,
  parseTiposInfoCompl,
  TIPOS_INFO_COMPL,
  TIPO_INFO_COMPL_PADRAO,
  type TipoInfoCompl,
} from "./InfoComplField";

type MinhaContaComponentContextType = {};

const MinhaContaComponentContext = createContext<
  MinhaContaComponentContextType | undefined
>(undefined);

export function MinhaContaComponentProvider() {
  return (
    <MinhaContaComponentContext.Provider value={{}}>
      <MinhaContaComponent />
    </MinhaContaComponentContext.Provider>
  );
}

export function useMinhaContaComponent() {
  const context = useContext(MinhaContaComponentContext);
  if (!context) {
    throw new Error(
      "useMinhaContaComponent deve ser usado dentro de MinhaContaComponentProvider",
    );
  }
  return context;
}

function MinhaContaComponent() {
  const context = useContext(MinhaContaComponentContext);
  if (!context) return null;
  const { t } = useTranslation();
  const { isLoading } = useGlobalLoader();
  const { contaConfig } = useContaConfig();
  const { clearAdminStatus } = useContext(AdminContext)!;

  const { setIsLoading } = useGlobalLoader();
  const { impressaoConfig, getImpressaoConfig, zerarEtiquetasImpressas } =
    useImpressaoConfig();
  const { localizacoes } = useLocalizacoes();
  const [newImpressaoConfig, setNewImpressaoConfig] =
    useState<ImpressaoConfigResponse | null>(null);
  const [tabSelected, setTabSelected] = useState<string>("geral");
  const [novaOpcaoInputs, setNovaOpcaoInputs] = useState<
    Record<number, string>
  >({});
  const [dialogMontarEpc, setDialogMontarEpc] = useState(false);
  const [epcBlocks, setEpcBlocks] = useState<EPCBlock[]>([]);
  const { categorias } = useCategorias();
  const [codigo, setCodigo] = useState<string>("");
  const [categoriaSelecionada, setCategoriaSelecionada] =
    useState<CategoriaItemResponse>({
      _id: "",
      descricao: "",
      ean: "",
      id_conta: "",
      inf_compl_1: "",
      inf_compl_10: "",
      inf_compl_11: "",
      inf_compl_12: "",
      inf_compl_2: "",
      inf_compl_3: "",
      inf_compl_4: "",
      inf_compl_5: "",
      inf_compl_6: "",
      inf_compl_7: "",
      inf_compl_8: "",
      inf_compl_9: "",
    });

  useEffect(() => {
    if (!categoriaSelecionada._id) {
      let categoria = categorias.find((c) => c.ean === codigo);
      if (categoria) {
        setCategoriaSelecionada({ ...categoria });
      }
    }
  }, [categoriaSelecionada.ean]);

  const handleAddBlock = (block: EPCBlock) => {
    setEpcBlocks((prev) => {
      // Não permite blocos repetidos (exceto custom)
      if (
        !block.id.includes("personalizado-") &&
        prev.some((b) => b.id === block.id)
      ) {
        alert.error("Este bloco já foi adicionado.");
        return prev;
      }
      const newBlock: EPCBlock = {
        ...block,
        id: `${block.id}`,
        removable: true,
      };
      return [...prev, newBlock];
    });
  };

  const handleRemoveBlock = (blockId: string) => {
    setEpcBlocks((prev) => prev.filter((block) => block.id !== blockId));
  };

  const handleReorderBlocks = (startIndex: number, endIndex: number) => {
    setEpcBlocks((prev) => {
      const newBlocks = [...prev];
      const draggedBlock = newBlocks[startIndex];

      newBlocks.splice(startIndex, 1);
      newBlocks.splice(endIndex, 0, draggedBlock);

      return newBlocks;
    });
  };

  const handleSalvarMontagemEPC = () => {
    const epcBlocksSemRemovable = epcBlocks.map(
      ({ removable, ...rest }) => rest,
    );

    if (newImpressaoConfig) {
      salvarImpressaoConfig({
        ...newImpressaoConfig,
        epc_personalizado: JSON.stringify(epcBlocksSemRemovable),
      });
    }

    setDialogMontarEpc(false);
  };

  // Marca/desmarca um botão do menu como visível para o cliente.
  // Guardamos a lista de OCULTOS (não a de visíveis) para que botões novos
  // adicionados ao menu no futuro apareçam por padrão.
  const toggleBotaoMenu = (url: string, visivel: boolean) => {
    setNewImpressaoConfig((prev) => {
      if (!prev) return prev;
      const atuais = prev.menu_botoes_ocultos ?? [];
      const menu_botoes_ocultos = visivel
        ? atuais.filter((u) => u !== url)
        : atuais.includes(url)
          ? atuais
          : [...atuais, url];
      return { ...prev, menu_botoes_ocultos };
    });
  };

  const salvarImpressaoConfig = async (config: ImpressaoConfigResponse) => {
    setIsLoading(true);
    try {
      await inovaone_api.post<ImpressaoConfigResponse[]>(`/impressao_config`, [
        config,
      ]);
      await getImpressaoConfig();
      alert.success("Configuração de impressão salva com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar configuração de impressão:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setNewImpressaoConfig(impressaoConfig);
  }, [impressaoConfig]);

  useEffect(() => {
    if (
      newImpressaoConfig?.epc_personalizado &&
      newImpressaoConfig?.epc_personalizado.length
    ) {
      let blocks: EPCBlock[] = [];
      let epcPersonalizadoParsed = JSON.parse(
        newImpressaoConfig.epc_personalizado,
      );
      blocks = epcPersonalizadoParsed.map((block: EPCBlock) => ({
        id: block.id,
        nome: block.nome,
        removable: true,
      }));
      setEpcBlocks(blocks);
    }
  }, [newImpressaoConfig]);

  if (!impressaoConfig) return null;

  const epcFilterOptions = [
    { descricao: "0 - all other", valor: "0" },
    { descricao: "1 - POS item", valor: "1" },
    { descricao: "2 - case", valor: "2" },
    { descricao: "3 - reserved", valor: "3" },
    { descricao: "4 - inner pack", valor: "4" },
    { descricao: "5 - reserved", valor: "5" },
    { descricao: "6 - unit load", valor: "6" },
    { descricao: "7 - component", valor: "7" },
  ];

  const companyPrefixOptions = [
    {
      descricao: "6 Dígitos",
      valor: "6",
    },
    {
      descricao: "7 Dígitos",
      valor: "5",
    },
    {
      descricao: "8 Dígitos",
      valor: "4",
    },
    {
      descricao: "9 Dígitos",
      valor: "3",
    },
    {
      descricao: "10 Dígitos",
      valor: "2",
    },
    {
      descricao: "11 Dígitos",
      valor: "1",
    },
    {
      descricao: "12 Dígitos",
      valor: "0",
    },
  ];

  if (!impressaoConfig) return null;

  // --- Opções de informação complementar (dropdown nas telas de impressão) ---
  const opcoesInfoComplMap = parseOpcoesInfoCompl(
    newImpressaoConfig?.opcoes_info_compl,
  );

  const persistirOpcoesInfoCompl = (proximo: Record<string, string[]>) => {
    if (!newImpressaoConfig) return;
    const limpo: Record<string, string[]> = {};
    for (const chave of Object.keys(proximo)) {
      const lista = proximo[chave];
      if (lista && lista.length > 0) limpo[chave] = lista;
    }
    setNewImpressaoConfig({
      ...newImpressaoConfig,
      opcoes_info_compl:
        Object.keys(limpo).length > 0 ? JSON.stringify(limpo) : "",
    });
  };

  const adicionarOpcaoInfoCompl = (idx: number, valor: string) => {
    const v = valor.trim();
    if (!v) return;
    const atual = opcoesInfoComplMap[String(idx)] ?? [];
    if (atual.includes(v)) {
      alert.error("Esta opção já foi adicionada.");
      return;
    }
    persistirOpcoesInfoCompl({
      ...opcoesInfoComplMap,
      [String(idx)]: [...atual, v],
    });
    setNovaOpcaoInputs((prev) => ({ ...prev, [idx]: "" }));
  };

  const removerOpcaoInfoCompl = (idx: number, valor: string) => {
    const atual = opcoesInfoComplMap[String(idx)] ?? [];
    persistirOpcoesInfoCompl({
      ...opcoesInfoComplMap,
      [String(idx)]: atual.filter((o) => o !== valor),
    });
  };

  // Índices das info. complementares marcadas como obrigatórias.
  const infoComplObrigatorios = parseInfoComplObrigatorios(
    newImpressaoConfig?.info_compl_obrigatorios,
  );

  const toggleObrigatorioInfoCompl = (idx: number, obrigatorio: boolean) => {
    if (!newImpressaoConfig) return;
    const atuais = new Set(infoComplObrigatorios);
    if (obrigatorio) atuais.add(idx);
    else atuais.delete(idx);
    const lista = Array.from(atuais).sort((a, b) => a - b);
    setNewImpressaoConfig({
      ...newImpressaoConfig,
      info_compl_obrigatorios: lista.length > 0 ? JSON.stringify(lista) : "",
    });
  };

  // Tipo de valor por info complementar (padrão: texto).
  const tiposInfoComplMap = parseTiposInfoCompl(
    newImpressaoConfig?.tipos_info_compl,
  );

  const definirTipoInfoCompl = (idx: number, tipo: TipoInfoCompl) => {
    if (!newImpressaoConfig) return;
    const proximo = { ...tiposInfoComplMap };
    // "texto" é o padrão → não precisa ser persistido.
    if (tipo === TIPO_INFO_COMPL_PADRAO) delete proximo[String(idx)];
    else proximo[String(idx)] = tipo;
    setNewImpressaoConfig({
      ...newImpressaoConfig,
      tipos_info_compl:
        Object.keys(proximo).length > 0 ? JSON.stringify(proximo) : "",
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <Settings className="h-5 w-5 text-muted-foreground" />
          Preferências da conta
        </h2>
        <p className="text-xs text-muted-foreground">
          Configurações de impressão, EPC, ordens e integrações
        </p>
      </div>

      <Dialog open={dialogMontarEpc} onOpenChange={setDialogMontarEpc}>
        <DialogContent className="sm:max-w-6xl w-[95vw] max-h-[92vh] p-0 flex flex-col gap-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              Montar EPC personalizado
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Combine blocos para gerar o EPC. A pré-visualização atualiza
              automaticamente.
            </p>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 px-6 py-4 overflow-y-auto">
            {/* Coluna esquerda: form + builder + palette */}
            <div className="lg:col-span-3 space-y-4">
              {/* Dados de teste */}
              <div className="rounded-md border p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Dados de teste
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Usados apenas para simular o EPC e a etiqueta na
                      pré-visualização. Os valores reais virão dos itens
                      impressos.
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="codigo" className="text-xs">
                      Código <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="codigo"
                      value={categoriaSelecionada.ean}
                      onChange={(e) => {
                        setCodigo(e.target.value);
                        setCategoriaSelecionada({
                          ...categoriaSelecionada,
                          ean: e.target.value,
                        });
                      }}
                      placeholder="Digite o código"
                      className="h-9"
                      required
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="descricao" className="text-xs">
                      Descrição
                    </Label>
                    <Input
                      id="descricao"
                      className="h-9"
                      value={categoriaSelecionada?.descricao || ""}
                      onChange={(e) =>
                        setCategoriaSelecionada({
                          ...categoriaSelecionada,
                          descricao: e.target.value,
                        })
                      }
                    />
                  </div>

                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="grid gap-1.5">
                      <Label
                        htmlFor={`info-compl-${i}`}
                        className="text-xs text-muted-foreground"
                      >
                        {(contaConfig as any)?.[`info_compl_${i}`] ||
                          `Info. Compl. ${i}`}
                      </Label>
                      <Input
                        id={`info-compl-${i}`}
                        className="h-9"
                        value={
                          ((categoriaSelecionada as any)?.[
                            `inf_compl_${i}`
                          ] as string) || ""
                        }
                        onChange={(e) =>
                          setCategoriaSelecionada({
                            ...categoriaSelecionada,
                            [`inf_compl_${i}`]: e.target.value,
                          } as CategoriaItemResponse)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Builder (drop zone) */}
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Montagem do EPC
                  </p>
                  {epcBlocks.length > 0 && (
                    <Badge variant="secondary" className="font-normal">
                      {epcBlocks.length}{" "}
                      {epcBlocks.length === 1 ? "bloco" : "blocos"}
                    </Badge>
                  )}
                </div>
                <EPCBuilder
                  blocks={epcBlocks}
                  onRemoveBlock={handleRemoveBlock}
                  onReorderBlocks={handleReorderBlocks}
                />
              </div>

              {/* Palette */}
              <div className="rounded-md border p-3">
                <EPCBlockPalette onAddBlock={handleAddBlock} />
              </div>
            </div>

            {/* Coluna direita: previews (sticky) */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-0 rounded-md border p-3">
                <EPCPreview
                  blocks={epcBlocks}
                  categoria={categoriaSelecionada}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 px-6 py-4 border-t">
            <Button
              variant="outline"
              onClick={() => setDialogMontarEpc(false)}
              size="sm"
            >
              <X className="h-4 w-4" />
              Fechar
            </Button>
            <Button
              onClick={handleSalvarMontagemEPC}
              size="sm"
              disabled={!codigo}
            >
              <Save className="h-4 w-4" />
              Salvar EPC
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Card>
        <CardContent className="p-4 sm:p-6">
          <Tabs
            defaultValue="geral"
            value={tabSelected}
            onValueChange={setTabSelected}
          >
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 h-auto">
              <TabsTrigger value="geral">Geral</TabsTrigger>
              <TabsTrigger value="impressao">Impressão</TabsTrigger>
              <TabsTrigger
                value="epc"
                disabled={impressaoConfig.gravacao_reversa}
              >
                Etiqueta
              </TabsTrigger>
              <TabsTrigger value="ordem">Ordem (Socket)</TabsTrigger>
              <TabsTrigger value="xml">XML</TabsTrigger>
              <TabsTrigger value="pedido">Pedidos</TabsTrigger>
              <TabsTrigger value="infocompl">Pré Definições</TabsTrigger>
            </TabsList>
            <TabsContent
              value="geral"
              className="grid gap-4 grid-cols-2 md:grid-cols-4 py-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="geral-localizacao_padrao_1">
                  Localização Padrão Nv.1
                </Label>
                <Select
                  disabled={isLoading}
                  value={newImpressaoConfig?.localizacao_padrao || "Estoque"}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        localizacao_padrao: value,
                        localizacao_padrao_2: "",
                        localizacao_padrao_3: "",
                        localizacao_padrao_4: "",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    {localizacoes.map((localizacao) => (
                      <SelectItem
                        key={localizacao.descricao}
                        value={localizacao.descricao}
                      >
                        {localizacao.descricao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="geral-localizacao_padrao_2">
                  Localização Padrão Nv.2
                </Label>
                <Select
                  disabled={
                    isLoading || !newImpressaoConfig?.localizacao_padrao
                  }
                  value={newImpressaoConfig?.localizacao_padrao_2 || ""}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        localizacao_padrao_2: value == "nenhuma" ? "" : value,
                        localizacao_padrao_3: "",
                        localizacao_padrao_4: "",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhuma">Nenhuma</SelectItem>
                    {localizacoes
                      .find(
                        (n1) =>
                          n1.descricao ===
                          newImpressaoConfig?.localizacao_padrao,
                      )
                      ?.children.map((n2) => (
                        <SelectItem key={n2.descricao} value={n2.descricao}>
                          {n2.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="geral-localizacao_padrao_3">
                  Localização Padrão Nv.3
                </Label>
                <Select
                  disabled={
                    isLoading ||
                    !newImpressaoConfig?.localizacao_padrao ||
                    !newImpressaoConfig?.localizacao_padrao_2
                  }
                  value={newImpressaoConfig?.localizacao_padrao_3 || ""}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        localizacao_padrao_3: value == "nenhuma" ? "" : value,
                        localizacao_padrao_4: "",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhuma">Nenhuma</SelectItem>
                    {localizacoes
                      .find(
                        (n1) =>
                          n1.descricao ===
                          newImpressaoConfig?.localizacao_padrao,
                      )
                      ?.children.find(
                        (n2) =>
                          n2.descricao ===
                          newImpressaoConfig?.localizacao_padrao_2,
                      )
                      ?.children.map((n3) => (
                        <SelectItem key={n3.descricao} value={n3.descricao}>
                          {n3.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="geral-localizacao_padrao_4">
                  Localização Padrão Nv.4
                </Label>
                <Select
                  disabled={
                    isLoading ||
                    !newImpressaoConfig?.localizacao_padrao ||
                    !newImpressaoConfig?.localizacao_padrao_2 ||
                    !newImpressaoConfig?.localizacao_padrao_3
                  }
                  value={newImpressaoConfig?.localizacao_padrao_4 || ""}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        localizacao_padrao_4: value == "nenhuma" ? "" : value,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhuma">Nenhuma</SelectItem>

                    {localizacoes
                      .find(
                        (n1) =>
                          n1.descricao ===
                          newImpressaoConfig?.localizacao_padrao,
                      )
                      ?.children.find(
                        (n2) =>
                          n2.descricao ===
                          newImpressaoConfig?.localizacao_padrao_2,
                      )
                      ?.children.find(
                        (n3) =>
                          n3.descricao ===
                          newImpressaoConfig?.localizacao_padrao_3,
                      )
                      ?.children.map((n4) => (
                        <SelectItem key={n4.descricao} value={n4.descricao}>
                          {n4.descricao}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="geral-api-externa">Api Externa</Label>
                <Input
                  disabled={isLoading}
                  id="geral-api-externa"
                  type="text"
                  defaultValue={impressaoConfig.api_externa}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        api_externa: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-gravacao-reversa">
                  Modo Teste de Impressão{" "}
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm">
                      Neste modo, as etiquetas são apenas impressas e não são
                      salvas na base de dados.
                    </TooltipContent>
                  </Tooltip>
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={impressaoConfig.modo_teste ? "true" : "false"}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        modo_teste: value === "true",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="geral-api-externa">Número de Impressões</Label>
                <Input
                  disabled={true}
                  id="geral-numero-de-impressoes"
                  type="text"
                  defaultValue={contaConfig?.numero_de_impressoes || 0}
                />
              </div>

              {/* Visibilidade dos botões do menu lateral para o cliente */}
              <div className="col-span-2 md:col-span-4 grid gap-3">
                <Separator />
                <div className="grid gap-1">
                  <Label className="text-base">Botões do menu</Label>
                  <p className="text-sm text-muted-foreground">
                    Escolha quais botões aparecem no menu lateral para o cliente.
                    Botões desativados ficam ocultos. Início e Preferências são
                    sempre visíveis.
                  </p>
                </div>
                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                  {MENU_SECTIONS.map((section) => (
                    <div key={section.labelKey} className="grid gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                        {t(section.labelKey)}
                      </span>
                      {section.items.map((item) => {
                        const protegido = PROTECTED_MENU_URLS.includes(item.url);
                        const ocultos =
                          newImpressaoConfig?.menu_botoes_ocultos ?? [];
                        const visivel = protegido || !ocultos.includes(item.url);
                        return (
                          <div
                            key={item.url}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="text-sm truncate">
                                {t(item.titleKey)}
                              </span>
                            </div>
                            <Switch
                              disabled={isLoading || protegido || !newImpressaoConfig}
                              checked={visivel}
                              onCheckedChange={(checked) =>
                                toggleBotaoMenu(item.url, checked)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            <TabsContent
              value="impressao"
              className="grid gap-4 grid-cols-2 md:grid-cols-4 py-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="impressao-comando-zpl-inicial">
                  Comando ZPL Inicial
                </Label>
                <Input
                  disabled={isLoading}
                  id="impressao-comando-zpl-inicial"
                  type="text"
                  defaultValue={impressaoConfig.comando_zpl_inicial}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        comando_zpl_inicial: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-comando-zpl-intermediario">
                  Comando ZPL Intermediário
                </Label>
                <Input
                  disabled={isLoading}
                  id="impressao-comando-zpl-intermediario"
                  type="text"
                  defaultValue={impressaoConfig.comando_zpl_intermediario}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        comando_zpl_intermediario: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-comando-zpl-final">
                  Comando ZPL Final
                </Label>
                <Input
                  disabled={isLoading}
                  id="impressao-comando-zpl-final"
                  type="text"
                  defaultValue={impressaoConfig.comando_zpl_final}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        comando_zpl_final: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-prioridade-rolo">
                  Prioridade do Rolo
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.tag_virgem_maior_epc ? "Maior" : "Menor"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        tag_virgem_maior_epc: value === "Maior",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"Maior"}>Maior EPC</SelectItem>
                    <SelectItem value={"Menor"}>Menor EPC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-padrao-rolo">Padrão do Rolo</Label>
                <Select
                  disabled={isLoading}
                  defaultValue={impressaoConfig.inicio_tag_virgem}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        inicio_tag_virgem: value,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"E280"}>E280</SelectItem>
                    <SelectItem value={"E28"}>E28</SelectItem>
                    <SelectItem value={"E282"}>E282</SelectItem>
                    <SelectItem value={"0000"}>0000</SelectItem>
                    <SelectItem value={"3008"}>3008</SelectItem>
                    <SelectItem value={"31D5"}>31D5</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="impressao-gravacao-reversa">
                  Gravação Reversa
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.gravacao_reversa ? "true" : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        gravacao_reversa: value === "true",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-exigir-inventario">
                  Exigir inventário
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.exigir_inventario ? "true" : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        exigir_inventario: value === "true",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-impressao-usb">Impressão USB</Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.impressao_usb == "true" ? "true" : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        impressao_usb: value,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-impressao-legacy">
                  Impressão Legacy
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.impressao_legacy == "true"
                      ? "true"
                      : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        impressao_legacy: value,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-impressao-usb">Apenas Gravar</Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.apenas_gravar ? "true" : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        apenas_gravar: value === "true",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-salvar-itens">
                  Salvar itens na base
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.salvar_itens_no_final ? "final" : "etiqueta"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        salvar_itens_no_final: value === "final",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"etiqueta"}>A cada etiqueta</SelectItem>
                    <SelectItem value={"final"}>
                      Só no final da impressão
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-tentativas-reimpressao">
                  Tentativas de Reimpressão
                </Label>
                <Input
                  disabled={isLoading}
                  id="impressao-tentativas-reimpressao"
                  type="number"
                  defaultValue={
                    impressaoConfig.tratativa_automatica_tentativas_ordem
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        tratativa_automatica_tentativas_ordem: parseInt(
                          e.target.value,
                        ),
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-tamanho-do-rolo">
                  Tamanho do Rolo
                </Label>
                <Input
                  disabled={isLoading}
                  id="impressao-tamanho-do-rolo"
                  type="number"
                  defaultValue={impressaoConfig.tamanho_rolo}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        tamanho_rolo: parseInt(e.target.value),
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-etiquetas-impresas-rolo">
                  Etiquetas Impressas (Rolo Atual)
                </Label>
                <Input
                  disabled={true}
                  id="impressao-etiquetas-impresas-rolo"
                  type="number"
                  defaultValue={impressaoConfig.etiquetas_impressas}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="impressao-limite-alerta-rolo">
                  Limite Alerta do Rolo (%)
                </Label>
                <Input
                  disabled={isLoading}
                  id="impressao-limite-alerta-rolo"
                  type="number"
                  placeholder="10"
                  defaultValue={impressaoConfig.limite_alerta_rolo}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        limite_alerta_rolo: parseInt(e.target.value),
                      });
                    }
                  }}
                />
              </div>
              <TratativasImpressaoSection
                impressaoConfig={impressaoConfig}
                newImpressaoConfig={newImpressaoConfig}
                setNewImpressaoConfig={setNewImpressaoConfig}
                isLoading={isLoading}
              />
              <div className="grid gap-2 col-span-2 md:col-span-4">
                <Label htmlFor="impressao-zpl-defeito">ZPL de Defeito</Label>
                <Textarea
                  disabled={isLoading}
                  id="impressao-zpl-defeito"
                  defaultValue={impressaoConfig.zpl_marcacao_defeito}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        zpl_marcacao_defeito: e.target.value,
                      });
                    }
                  }}
                />
              </div>
            </TabsContent>
            <TabsContent
              value="epc"
              className="grid gap-4 grid-cols-2 md:grid-cols-4 py-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="epc-tipo-epc">Tipo de EPC</Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    (impressaoConfig?.padrao_epc ?? "")
                      .toLowerCase()
                      .includes("gs1")
                      ? "gs1"
                      : "personalizado"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        padrao_epc: value.includes("gs1")
                          ? JSON.stringify({ descricao: "GS1 GTIN-13" })
                          : JSON.stringify({ descricao: "Personalizado" }),
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"personalizado"}>
                      Personalizado
                    </SelectItem>
                    <SelectItem value={"gs1"}>GS1 GTIN-13</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newImpressaoConfig?.padrao_epc &&
                newImpressaoConfig.padrao_epc.toLowerCase().includes("gs1") && (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="epc-company-prefix">
                        Prefixo da Empresa
                      </Label>
                      <Select
                        disabled={
                          isLoading ||
                          !newImpressaoConfig?.padrao_epc
                            .toLocaleLowerCase()
                            .includes("gs1")
                        }
                        defaultValue={
                          impressaoConfig.company_prefix.match(/[0-6]/)?.[0] ||
                          "6"
                        }
                        onValueChange={(value) => {
                          if (newImpressaoConfig) {
                            const selected = companyPrefixOptions.find(
                              (opt) => opt.valor === value,
                            );
                            setNewImpressaoConfig({
                              ...newImpressaoConfig,
                              company_prefix: selected
                                ? JSON.stringify({
                                    descricao: selected.descricao,
                                    valor: selected.valor,
                                  })
                                : "6",
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Não definido" />
                        </SelectTrigger>
                        <SelectContent>
                          {companyPrefixOptions.map((opt) => (
                            <SelectItem key={opt.valor} value={opt.valor}>
                              {opt.descricao}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="epc-filter-epc">Filtro EPC</Label>
                      <Select
                        disabled={
                          isLoading ||
                          !newImpressaoConfig?.padrao_epc
                            .toLocaleLowerCase()
                            .includes("gs1")
                        }
                        defaultValue={
                          impressaoConfig.filter_epc.match(/[0-7]/)?.[0] || "0"
                        }
                        onValueChange={(value) => {
                          if (newImpressaoConfig) {
                            const selected = epcFilterOptions.find(
                              (opt) => opt.valor === value,
                            );
                            setNewImpressaoConfig({
                              ...newImpressaoConfig,
                              filter_epc: selected
                                ? JSON.stringify({
                                    descricao: selected.descricao,
                                    valor: selected.valor,
                                  })
                                : "0",
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Não definido" />
                        </SelectTrigger>
                        <SelectContent>
                          {epcFilterOptions.map((opt) => (
                            <SelectItem key={opt.valor} value={opt.valor}>
                              {opt.descricao}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
            </TabsContent>
            <TabsContent
              value="ordem"
              className="grid gap-4 grid-cols-2 md:grid-cols-4 py-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-codigo">Campo do Código</Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-codigo"
                  type="number"
                  defaultValue={
                    impressaoConfig.posicao_gtin_impressao
                      ? parseInt(impressaoConfig.posicao_gtin_impressao) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        posicao_gtin_impressao: `${parseInt(e.target.value) - 1}`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-descricao">
                  Campo da Descrição
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-descricao"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_descricao_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_descricao_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_descricao_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-epc">Campo do EPC</Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-epc"
                  type="number"
                  defaultValue={impressaoConfig.campo_ordem_tag}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_ordem_tag: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-id-externo">
                  Campo Código Externo
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-id-externo"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_id_externo
                      ? parseInt(impressaoConfig.campo_id_externo) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_id_externo: `${parseInt(e.target.value) - 1}`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-1">
                  {contaConfig?.info_compl_1 || "Info. Compl. 1"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-1"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_1_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_1_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_1_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-2">
                  {contaConfig?.info_compl_2 || "Info. Compl. 2"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-2"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_2_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_2_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_2_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-3">
                  {contaConfig?.info_compl_3 || "Info. Compl. 3"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-3"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_3_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_3_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_3_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-4">
                  {contaConfig?.info_compl_4 || "Info. Compl. 4"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-4"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_4_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_4_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_4_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-5">
                  {contaConfig?.info_compl_5 || "Info. Compl. 5"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-5"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_5_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_5_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_5_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-6">
                  {contaConfig?.info_compl_6 || "Info. Compl. 6"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-6"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_6_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_6_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_6_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-7">
                  {contaConfig?.info_compl_7 || "Info. Compl. 7"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-7"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_7_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_7_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_7_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-8">
                  {contaConfig?.info_compl_8 || "Info. Compl. 8"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-8"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_8_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_8_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_8_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-9">
                  {contaConfig?.info_compl_9 || "Info. Compl. 9"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-9"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_9_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_9_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_9_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-info-compl-10">
                  {contaConfig?.info_compl_10 || "Info. Compl. 10"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-info-compl-10"
                  type="number"
                  defaultValue={
                    impressaoConfig.campo_info_compl_10_ordem_impressao
                      ? parseInt(
                          impressaoConfig.campo_info_compl_10_ordem_impressao,
                        ) + 1
                      : ""
                  }
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info_compl_10_ordem_impressao: `${
                          parseInt(e.target.value) - 1
                        }`,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-filtro-ordens">
                  Filtro de Ordens (separado por vírgula)
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-filtro-ordens"
                  type="text"
                  defaultValue={impressaoConfig.filtro_ordem}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        filtro_ordem: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-ordem-loc1">
                  Campo da Localização Nv.1
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-ordem-loc1"
                  type="number"
                  defaultValue={impressaoConfig.campo_ordem_loc1}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_ordem_loc1: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-ordem-loc2">
                  Campo da Localização Nv.2
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-ordem-loc2"
                  type="number"
                  defaultValue={impressaoConfig.campo_ordem_loc2}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_ordem_loc2: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-ordem-loc3">
                  Campo da Localização Nv.3
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-ordem-loc3"
                  type="number"
                  defaultValue={impressaoConfig.campo_ordem_loc3}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_ordem_loc3: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-campo-ordem-loc4">
                  Campo da Localização Nv.4
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-campo-ordem-loc4"
                  type="number"
                  defaultValue={impressaoConfig.campo_ordem_loc4}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_ordem_loc4: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-webhook-erro">Webhook de Erro</Label>
                <Input
                  disabled={isLoading}
                  id="ordem-webhook-erro"
                  type="text"
                  defaultValue={impressaoConfig.api_externa_erro}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        api_externa_erro: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-webhook-finalizada">
                  Webhook de Ordem Finalização
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-webhook-finalizada"
                  type="text"
                  defaultValue={impressaoConfig.webhook_ordem_finalizada}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        webhook_ordem_finalizada: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-webhook-etiquetas-acabando">
                  Webhook de Etiquetas Acabando
                </Label>
                <Input
                  disabled={isLoading}
                  id="ordem-webhook-etiquetas-acabando"
                  type="text"
                  defaultValue={impressaoConfig.webhook_etiquetas_acabando}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        webhook_etiquetas_acabando: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ordem-serializar-epc">Serializar Epcs</Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.serializar_epc === "true" ? "true" : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        serializar_epc: value,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent
              value="xml"
              className="grid gap-4 grid-cols-2 md:grid-cols-4 py-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-descricao">Campo Descrição</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-descricao"
                  type="text"
                  defaultValue={impressaoConfig.campo_descricao_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_descricao_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-codigo">Campo Código</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-codigo"
                  type="text"
                  defaultValue={impressaoConfig.campo_codigo_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_codigo_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info1">
                  {contaConfig?.info_compl_1 || "Info. Compl. 1"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info1"
                  type="text"
                  defaultValue={impressaoConfig.campo_info1_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info1_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info2">
                  {contaConfig?.info_compl_2 || "Info. Compl. 2"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info2"
                  type="text"
                  defaultValue={impressaoConfig.campo_info2_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info2_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info3">
                  {contaConfig?.info_compl_3 || "Info. Compl. 3"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info3"
                  type="text"
                  defaultValue={impressaoConfig.campo_info3_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info3_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info4">
                  {contaConfig?.info_compl_4 || "Info. Compl. 4"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info4"
                  type="text"
                  defaultValue={impressaoConfig.campo_info4_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info4_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info5">
                  {contaConfig?.info_compl_5 || "Info. Compl. 5"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info5"
                  type="text"
                  defaultValue={impressaoConfig.campo_info5_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info5_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info6">
                  {contaConfig?.info_compl_6 || "Info. Compl. 6"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info6"
                  type="text"
                  defaultValue={impressaoConfig.campo_info6_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info6_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info7">
                  {contaConfig?.info_compl_7 || "Info. Compl. 7"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info7"
                  type="text"
                  defaultValue={impressaoConfig.campo_info7_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info7_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info8">
                  {contaConfig?.info_compl_8 || "Info. Compl. 8"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info8"
                  type="text"
                  defaultValue={impressaoConfig.campo_info8_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info8_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info9">
                  {contaConfig?.info_compl_9 || "Info. Compl. 9"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info9"
                  type="text"
                  defaultValue={impressaoConfig.campo_info9_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info9_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info10">
                  {contaConfig?.info_compl_10 || "Info. Compl. 10"}
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info10"
                  type="text"
                  defaultValue={impressaoConfig.campo_info10_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info10_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info11">{"Info. Compl. 11"}</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info11"
                  type="text"
                  defaultValue={impressaoConfig.campo_info11_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info11_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-info12">{"Info. Compl. 12"}</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-info12"
                  type="text"
                  defaultValue={impressaoConfig.campo_info12_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_info12_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-valor">Campo Valor</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-valor"
                  type="text"
                  placeholder="ex.: vProd ou vProd/qCom"
                  defaultValue={impressaoConfig.campo_valor_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_valor_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-valor-unidade">
                  Campo Valor da Unidade
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-valor-unidade"
                  type="text"
                  placeholder="ex.: vUnCom ou vUnCom*info.compl.2"
                  defaultValue={impressaoConfig.campo_valor_unidade_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_valor_unidade_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-qtde">Campo Quantidade (Item)</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-qtde"
                  type="text"
                  placeholder="ex.: qCom ou qCom/info.compl.1"
                  defaultValue={impressaoConfig.campo_qtde_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_qtde_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-qtde-tags">
                  Campo Quantidade de Tags (Multiplicador)
                </Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-qtde-tags"
                  type="text"
                  placeholder="ex.: qVol ou qCom/info.compl.1"
                  defaultValue={impressaoConfig.campo_qtde_tags_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_qtde_tags_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="xml-campo-validade">Campo Validade</Label>
                <Input
                  disabled={isLoading}
                  id="xml-campo-validade"
                  type="text"
                  defaultValue={impressaoConfig.campo_validade_xml}
                  onChange={(e) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_validade_xml: e.target.value,
                      });
                    }
                  }}
                />
              </div>
              <div className="col-span-2 md:col-span-4 rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1.5">
                <p className="font-medium text-foreground">
                  Operações (macros) — Valor, Valor da Unidade, Quantidade
                  (Item) e Quantidade de Tags
                </p>
                <p>
                  Além do nome de uma tag do XML, esses campos aceitam
                  expressões com{" "}
                  <code className="px-1 py-0.5 rounded bg-muted">+ - * /</code>{" "}
                  combinando tags do XML, números e infos complementares da
                  categoria cadastrada na base (
                  <code className="px-1 py-0.5 rounded bg-muted">
                    info.compl.N
                  </code>
                  ). Ex.:{" "}
                  <code className="px-1 py-0.5 rounded bg-muted">
                    qCom/info.compl.1
                  </code>{" "}
                  — divide o valor da tag <code>qCom</code> pela Info. Compl. 1
                  da categoria do item.
                </p>
                <p>
                  Se alguma parte não existir (tag ausente, categoria sem a
                  info ou divisão por zero), vale só o primeiro termo da
                  expressão. <code>*</code> e <code>/</code> são calculados
                  antes de <code>+</code> e <code>-</code>. O resultado é
                  limitado a 2 casas decimais (0,909091 → 0,90); na Quantidade
                  de Tags ele é arredondado pra cima.
                </p>
              </div>
            </TabsContent>
            <TabsContent
              value="pedido"
              className="grid gap-4 grid-cols-2 md:grid-cols-4 py-4"
            >
              <div className="grid gap-2">
                <Label htmlFor="pedido-travar-quantidade-erp">
                  Travar Quantidade ERP
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={
                    impressaoConfig.travar_quantidade_erp ? "true" : "false"
                  }
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        travar_quantidade_erp: value === "true",
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"true"}>Sim</SelectItem>
                    <SelectItem value={"false"}>Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pedido-campo-quantidade-pedidos">
                  Campo Quantidade (Info. Complementar)
                </Label>
                <Select
                  disabled={isLoading}
                  defaultValue={impressaoConfig.campo_quantidade_pedidos || ""}
                  onValueChange={(value) => {
                    if (newImpressaoConfig) {
                      setNewImpressaoConfig({
                        ...newImpressaoConfig,
                        campo_quantidade_pedidos: value,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Não definido" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={"inf_compl_1"}>
                      {contaConfig?.info_compl_1 || "Info. Compl. 1"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_2"}>
                      {contaConfig?.info_compl_2 || "Info. Compl. 2"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_3"}>
                      {contaConfig?.info_compl_3 || "Info. Compl. 3"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_4"}>
                      {contaConfig?.info_compl_4 || "Info. Compl. 4"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_5"}>
                      {contaConfig?.info_compl_5 || "Info. Compl. 5"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_6"}>
                      {contaConfig?.info_compl_6 || "Info. Compl. 6"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_7"}>
                      {contaConfig?.info_compl_7 || "Info. Compl. 7"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_8"}>
                      {contaConfig?.info_compl_8 || "Info. Compl. 8"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_9"}>
                      {contaConfig?.info_compl_9 || "Info. Compl. 9"}
                    </SelectItem>
                    <SelectItem value={"inf_compl_10"}>
                      {contaConfig?.info_compl_10 || "Info. Compl. 10"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {([1, 2, 3, 4] as const).map((nivel) => {
                const campoKey = `nivel_loc_${nivel}_pedidos` as const;
                const infoComplLabels = [
                  contaConfig?.info_compl_1,
                  contaConfig?.info_compl_2,
                  contaConfig?.info_compl_3,
                  contaConfig?.info_compl_4,
                  contaConfig?.info_compl_5,
                  contaConfig?.info_compl_6,
                  contaConfig?.info_compl_7,
                  contaConfig?.info_compl_8,
                  contaConfig?.info_compl_9,
                  contaConfig?.info_compl_10,
                ];
                return (
                  <div className="grid gap-2" key={campoKey}>
                    <Label htmlFor={`pedido-${campoKey}`}>
                      Localização Nível {nivel} (Info. Complementar)
                    </Label>
                    <Select
                      disabled={isLoading}
                      defaultValue={impressaoConfig[campoKey] || "__none__"}
                      onValueChange={(value) => {
                        if (newImpressaoConfig) {
                          setNewImpressaoConfig({
                            ...newImpressaoConfig,
                            [campoKey]: value === "__none__" ? "" : value,
                          });
                        }
                      }}
                    >
                      <SelectTrigger
                        id={`pedido-${campoKey}`}
                        className="w-full"
                      >
                        <SelectValue placeholder="Não definido" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Não definido</SelectItem>
                        {infoComplLabels.map((label, idx) => (
                          <SelectItem
                            key={idx + 1}
                            value={`inf_compl_${idx + 1}`}
                          >
                            {label || `Info. Compl. ${idx + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </TabsContent>
            <TabsContent value="infocompl" className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Defina valores fixos para cada informação complementar. O{" "}
                <span className="font-medium">Tipo do valor</span> controla o
                formato aceito no campo de texto livre (número, monetário,
                texto, data ou data e hora). Quando houver opções cadastradas, o
                campo correspondente nas telas de impressão vira um menu de
                seleção (sem digitação) e o tipo é ignorado. Marque{" "}
                <span className="font-medium">Obrigatório</span> para impedir a
                impressão enquanto o campo não estiver preenchido.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {Array.from({ length: 10 }, (_, k) => k + 1).map((i) => {
                  const label =
                    (contaConfig as any)?.[`info_compl_${i}`] ||
                    `Info. Compl. ${i}`;
                  const opcoes = opcoesInfoComplMap[String(i)] ?? [];
                  const obrigatorio = infoComplObrigatorios.includes(i);
                  const tipo =
                    tiposInfoComplMap[String(i)] ?? TIPO_INFO_COMPL_PADRAO;
                  return (
                    <div
                      key={i}
                      className="grid gap-2 rounded-md border bg-card p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor={`infocompl-input-${i}`}>{label}</Label>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                          Obrigatório
                          <Switch
                            disabled={isLoading || !newImpressaoConfig}
                            checked={obrigatorio}
                            onCheckedChange={(checked) =>
                              toggleObrigatorioInfoCompl(i, checked)
                            }
                          />
                        </label>
                      </div>
                      <div className="grid gap-1.5">
                        <Label
                          htmlFor={`infocompl-tipo-${i}`}
                          className="text-xs text-muted-foreground"
                        >
                          Tipo do valor
                        </Label>
                        <Select
                          disabled={isLoading || !newImpressaoConfig}
                          value={tipo}
                          onValueChange={(v) =>
                            definirTipoInfoCompl(i, v as TipoInfoCompl)
                          }
                        >
                          <SelectTrigger
                            id={`infocompl-tipo-${i}`}
                            className="w-full"
                          >
                            <SelectValue placeholder="Texto" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIPOS_INFO_COMPL.map((op) => (
                              <SelectItem key={op.valor} value={op.valor}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-wrap gap-1.5 min-h-6">
                        {opcoes.length === 0 ? (
                          <span className="text-xs text-muted-foreground">
                            Texto livre (nenhuma opção cadastrada)
                          </span>
                        ) : (
                          opcoes.map((opt) => (
                            <Badge
                              key={opt}
                              variant="secondary"
                              className="font-normal gap-1 pr-1"
                            >
                              {opt}
                              <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => removerOpcaoInfoCompl(i, opt)}
                                className="rounded-sm hover:bg-muted-foreground/20"
                                aria-label={`Remover ${opt}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          autoComplete="off"
                          disabled={isLoading}
                          id={`infocompl-input-${i}`}
                          type="text"
                          placeholder="Nova opção"
                          value={novaOpcaoInputs[i] ?? ""}
                          onChange={(e) =>
                            setNovaOpcaoInputs((prev) => ({
                              ...prev,
                              [i]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              adicionarOpcaoInfoCompl(i, novaOpcaoInputs[i] ?? "");
                            }
                          }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isLoading}
                          onClick={() =>
                            adicionarOpcaoInfoCompl(i, novaOpcaoInputs[i] ?? "")
                          }
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Footer com ações */}
      <div className="flex flex-wrap items-center justify-end gap-2 rounded-md border bg-card p-3">
        {tabSelected === "epc" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDialogMontarEpc(true)}
          >
            <Plus className="h-4 w-4" />
            Montar EPC
          </Button>
        )}
        {tabSelected === "impressao" && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await zerarEtiquetasImpressas();
              alert.success("Contador de etiquetas zerado.");
            }}
          >
            Troquei o Rolo de Etiquetas
          </Button>
        )}
        <Button
          disabled={isLoading || !newImpressaoConfig}
          variant="outline"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
          onClick={() => clearAdminStatus()}
        >
          Desativar Administrador
        </Button>
        <Button
          disabled={isLoading || !newImpressaoConfig}
          size="sm"
          onClick={() => {
            if (newImpressaoConfig) {
              salvarImpressaoConfig(newImpressaoConfig);
            } else {
              alert.error("Nenhuma configuração para salvar.");
            }
          }}
        >
          <Save className="h-4 w-4" />
          Salvar preferências
        </Button>
      </div>
    </div>
  );
}

interface EPCBlockPaletteProps {
  onAddBlock: (block: EPCBlock) => void;
}

type BlockCategory = {
  titulo: string;
  blocos: EPCBlock[];
};

export const EPCBlockPalette: React.FC<EPCBlockPaletteProps> = ({
  onAddBlock,
}) => {
  const [customValue, setCustomValue] = useState("");
  const [busca, setBusca] = useState("");

  const categorias: BlockCategory[] = [
    {
      titulo: "Informações complementares",
      blocos: [
        { id: "{info1}", nome: "Info 1" },
        { id: "{info2}", nome: "Info 2" },
        { id: "{info3}", nome: "Info 3" },
        { id: "{info4}", nome: "Info 4" },
        { id: "{info5}", nome: "Info 5" },
        { id: "{info6}", nome: "Info 6" },
        { id: "{info7}", nome: "Info 7" },
        { id: "{info8}", nome: "Info 8" },
        { id: "{info9}", nome: "Info 9" },
        { id: "{info10}", nome: "Info 10" },
        { id: "{codigo-panvel}", nome: "Código Panvel" },
      ],
    },
    {
      titulo: "Códigos e seriais",
      blocos: [
        { id: "{codigo}", nome: "Código" },
        { id: "{codigosze}", nome: "Código SZE" },
        { id: "{serial}", nome: "Serial" },
        { id: "{serialhex}", nome: "Serial (HEX)" },
        { id: "{serial+1}", nome: "Serial +1" },
        { id: "{serial+1cze}", nome: "Serial +1 CZE" },
        { id: "{serialc2ze}", nome: "Serial CZE (2)" },
        { id: "{serialc3ze}", nome: "Serial CZE (3)" },
        { id: "{serialcze}", nome: "Serial CZE (4)" },
        { id: "{serialc5ze}", nome: "Serial CZE (5)" },
        { id: "{serialc6ze}", nome: "Serial CZE (6)" },
        { id: "{serialc7ze}", nome: "Serial CZE (7)" },
        { id: "{serialc8ze}", nome: "Serial CZE (8)" },
        { id: "{n_impressao}", nome: "Nº de Impressão" },
      ],
    },
    {
      titulo: "Data e hora",
      blocos: [
        { id: "{dd}", nome: "Dia (DD)" },
        { id: "{mm}", nome: "Mês (MM)" },
        { id: "{aa}", nome: "Ano (AA)" },
        { id: "{aaaa}", nome: "Ano (AAAA)" },
        { id: "{hh}", nome: "Hora (HH)" },
        { id: "{mn}", nome: "Min (MM)" },
        { id: "{ss}", nome: "Seg (SS)" },
        { id: "{hh_mn_ss_hex}", nome: "HH:MM:SS (HEX)" },
        { id: "{dd_mm_hex}", nome: "DD/MM (HEX)" },
      ],
    },
    {
      titulo: "Descrição",
      blocos: [{ id: "{desc}", nome: "Descrição" }],
    },
    {
      titulo: "Caracteres e números",
      blocos: [
        { id: "{A}", nome: "A" },
        { id: "{B}", nome: "B" },
        { id: "{C}", nome: "C" },
        { id: "{D}", nome: "D" },
        { id: "{E}", nome: "E" },
        { id: "{F}", nome: "F" },
        { id: "{0}", nome: "0" },
        { id: "{1}", nome: "1" },
        { id: "{2}", nome: "2" },
        { id: "{3}", nome: "3" },
        { id: "{4}", nome: "4" },
        { id: "{5}", nome: "5" },
        { id: "{6}", nome: "6" },
        { id: "{7}", nome: "7" },
        { id: "{8}", nome: "8" },
        { id: "{9}", nome: "9" },
        { id: "{<<>>}", nome: "<<>>" },
      ],
    },
  ];

  const buscaNorm = busca.trim().toLowerCase();
  const categoriasFiltradas = buscaNorm
    ? categorias
        .map((c) => ({
          ...c,
          blocos: c.blocos.filter(
            (b) =>
              b.nome.toLowerCase().includes(buscaNorm) ||
              b.id.toLowerCase().includes(buscaNorm),
          ),
        }))
        .filter((c) => c.blocos.length > 0)
    : categorias;

  const handleCustomValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    const validChars = /^[A-F0-9]*$/;
    if (validChars.test(value)) {
      setCustomValue(value);
    }
  };

  const addCustomBlock = () => {
    if (customValue.trim()) {
      onAddBlock({
        id: "personalizado-" + customValue,
        nome: customValue,
      });
      setCustomValue("");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Blocos disponíveis</h3>
        <div className="relative w-44">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            placeholder="Buscar..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="h-8 pl-7 pr-7 text-xs"
          />
          {busca && (
            <button
              type="button"
              onClick={() => setBusca("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Limpar busca"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {categoriasFiltradas.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">
            Nenhum bloco encontrado.
          </p>
        ) : (
          categoriasFiltradas.map((cat) => (
            <div key={cat.titulo} className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {cat.titulo}
              </p>
              <div className="flex flex-wrap gap-1">
                {cat.blocos.map((blockType, index) => (
                  <Button
                    key={`${cat.titulo}-${blockType.id}-${index}`}
                    onClick={() => onAddBlock(blockType)}
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs hover:border-primary/40 hover:bg-primary/5"
                  >
                    <Plus className="h-3 w-3 mr-0.5 opacity-60" />
                    {blockType.nome}
                  </Button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <Separator />

      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Bloco personalizado (hexadecimal)
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="Apenas A-F e 0-9"
            value={customValue}
            onChange={handleCustomValueChange}
            className="h-8 text-xs"
            maxLength={10}
            onKeyDown={(e) => {
              if (e.key === "Enter") addCustomBlock();
            }}
          />
          <Button
            onClick={addCustomBlock}
            size="sm"
            disabled={!customValue.trim()}
            className="h-8"
          >
            <Plus className="h-3.5 w-3.5" />
            Adicionar
          </Button>
        </div>
      </div>
    </div>
  );
};

interface EPCPreviewProps {
  blocks: EPCBlock[];
  categoria: CategoriaItemResponse | undefined;
}

export const EPCPreview: React.FC<EPCPreviewProps> = ({
  blocks,
  categoria,
}) => {
  const { gerarEpcs } = useEpc();
  const { coletaZplPadrao } = useImpressaoConfig();

  // ---- Preview dos EPCs ----
  const preview = useMemo(() => {
    if (!categoria || !blocks || blocks.length === 0) return [];
    return gerarEpcs(
      categoria,
      3,
      ["LOCALIZACAO 1", "LOCALIZACAO 2", "LOCALIZACAO 3", "LOCALIZACAO 4"],
      blocks,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoria, blocks]);

  // ---- Preview da etiqueta (labelary) ----
  // Debounce + lazy load (igual à página Etiquetas)
  const [debouncedZpl, setDebouncedZpl] = useState<{
    item: any;
    epc: string;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErro, setPreviewErro] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const zplPadrao = coletaZplPadrao();
      if (zplPadrao && zplPadrao.zpl && preview[0]?.zpl_impressao_bo) {
        setDebouncedZpl({
          item: { ...zplPadrao, zpl: preview[0].zpl_impressao_bo },
          epc: preview[0].epc,
        });
      } else {
        setDebouncedZpl(null);
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const previewUrl = useMemo(() => {
    if (!debouncedZpl) return null;
    return converterListaZplItemParaUrl(debouncedZpl.item);
  }, [debouncedZpl]);

  useEffect(() => {
    if (previewUrl) {
      setPreviewLoading(true);
      setPreviewErro(false);
    }
  }, [previewUrl]);

  if (!categoria) {
    return (
      <div className="p-4 rounded-md border bg-muted/20">
        <p className="text-sm text-muted-foreground text-center">
          Informe um código para ver a pré-visualização.
        </p>
      </div>
    );
  }
  if (!blocks || blocks.length === 0) {
    return (
      <div className="p-4 rounded-md border bg-muted/20">
        <p className="text-sm text-muted-foreground text-center">
          Adicione blocos para ver a pré-visualização.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* EPCs gerados */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            EPCs gerados
          </p>
          <span className="text-[10px] text-muted-foreground">
            mostrando {preview.length} amostras
          </span>
        </div>
        <div className="rounded-md border bg-muted/30 divide-y">
          {preview.length > 0 ? (
            preview.map((epc, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-2 py-1.5 text-sm"
              >
                <span className="text-[10px] text-muted-foreground w-4">
                  #{index + 1}
                </span>
                <span className="break-all text-foreground flex-1">
                  {epc.epc}
                </span>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground text-center py-3">
              Nenhum dado disponível.
            </p>
          )}
        </div>
      </div>

      {/* Estrutura */}
      <div>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
          Estrutura
        </p>
        <div className="flex flex-wrap gap-1">
          {blocks.map((block, index) => (
            <Badge
              key={index}
              variant="outline"
              className="bg-primary/5 text-primary border-primary/20 text-[11px]"
            >
              {block.nome}
            </Badge>
          ))}
        </div>
      </div>

      {/* Pré-visualização da etiqueta (labelary) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Eye className="h-3 w-3" />
            Pré-visualização da etiqueta
          </p>
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-primary hover:underline flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              Abrir
            </a>
          )}
        </div>

        <div className="relative rounded-md border bg-white overflow-hidden aspect-[3/4] flex items-center justify-center">
          {!previewUrl ? (
            <div className="flex flex-col items-center gap-2 text-muted-foreground p-4 text-center">
              <Eye className="h-7 w-7 opacity-40" />
              <p className="text-[11px]">
                Configure a etiqueta padrão pra visualizar.
              </p>
            </div>
          ) : (
            <>
              <img
                key={previewUrl}
                src={previewUrl}
                alt="Pré-visualização da etiqueta"
                className="max-w-full max-h-full object-contain"
                onLoad={() => {
                  setPreviewLoading(false);
                  setPreviewErro(false);
                }}
                onError={() => {
                  setPreviewLoading(false);
                  setPreviewErro(true);
                }}
              />
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {previewErro && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white text-muted-foreground p-4 text-center">
                  <ImageOff className="h-7 w-7 opacity-50" />
                  <p className="text-[11px]">
                    Não foi possível renderizar a etiqueta.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export interface EPCBlock {
  id: string;
  nome: string;
  removable?: boolean;
}

interface EPCBuilderProps {
  blocks: EPCBlock[];
  onRemoveBlock: (blockId: string) => void;
  onReorderBlocks: (startIndex: number, endIndex: number) => void;
}

export const EPCBuilder: React.FC<EPCBuilderProps> = ({
  blocks,
  onRemoveBlock,
  onReorderBlocks,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      onReorderBlocks(draggedIndex, dropIndex);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div
      className="min-h-20 border-2 border-dashed rounded-md p-3 bg-muted/20 transition-colors"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
    >
      {blocks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-3 gap-1">
          <Plus className="h-5 w-5 text-muted-foreground/50" />
          <p className="text-xs text-muted-foreground text-center">
            Clique nos blocos abaixo pra montar o EPC.
            <br />
            Arraste pra reordenar.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 items-center">
          {blocks.map((block, index) => (
            <div
              key={block.id + "-" + index}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-md text-xs border cursor-move select-none transition ${
                draggedIndex === index
                  ? "opacity-50 bg-primary/20 border-primary/40"
                  : dragOverIndex === index
                    ? "bg-primary/10 border-primary ring-2 ring-primary/30"
                    : "bg-primary/5 text-primary border-primary/20 hover:bg-primary/10"
              }`}
            >
              <span className="text-[10px] text-muted-foreground font-medium pointer-events-none">
                {index + 1}
              </span>
              <span className="pointer-events-none font-medium">
                {block.nome}
              </span>
              {block.removable && (
                <button
                  type="button"
                  className="ml-0.5 flex h-5 w-5 items-center justify-center rounded hover:bg-red-500/15 text-muted-foreground hover:text-red-600 transition pointer-events-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveBlock(block.id);
                  }}
                  aria-label={`Remover ${block.nome}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
